import { Session, SessionFormField, SessionNotes, sessionParticipantOptionsDict, SessionParticipation, SessionResource, sessionServiceOptionsDict, SessionServiceType } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { Diagnosis, YesNo } from '@uplift/types';
import { TIME_RANGE_DATE_VALUE_FORMAT } from '@uplift/ui-v2';
import { ClinicalNotesDynamicFormKeyType, ClinicalNotesDynamicFormsType, ClinicalNotesDynamicFormType, ClinicalNotesFormType, ClinicalNotesSessionFormField, ClinicalNotesSessionForms, ClinicalNotesTemplateType, ClinicalNotesTreatmentType, DynamicFormVisibilityKeys, DynamicFormVisibilityMappingType, DynamicYesNoKeyHash, FieldTypeHelpersType } from './types';

export const dynamicFormToTemplateMapping = {
  'followUpProgress': 'followUp',
  'initialProgress': 'initial',
};

export const dynamicFormVisibilityMapping: DynamicFormVisibilityMappingType = {
  'riskAssessment': {
    yesNoField: 'riskAssessmentDenyYesNo',
    visibleOnValue: false,
    visibleOnYesNoValue: YesNo.No
  },
  'mentalStatusExam': {
    yesNoField: 'mentalStatusExamWithinNormalRangeYesNo',
    visibleOnValue: false,
    visibleOnYesNoValue: YesNo.No
  }
};

export const checkIfFormDisabledByYesNoToggle = (dynamicFormKey: ClinicalNotesDynamicFormKeyType, values: ClinicalNotesFormType) => {
  if (!(dynamicFormKey in dynamicFormVisibilityMapping)) return false;
  const dynamicFormVisibility = dynamicFormVisibilityMapping[dynamicFormKey as DynamicFormVisibilityKeys];

  return dynamicFormVisibility && (dynamicFormVisibility.visibleOnYesNoValue !== values[dynamicFormVisibility.yesNoField]);
};

export const checkIfFormIsOnTemplate = (dynamicFormKey: ClinicalNotesDynamicFormKeyType, template: ClinicalNotesTemplateType) => {
  if (!dynamicFormToTemplateMapping?.[dynamicFormKey]) return true; // if formKey not on mapping, it means form is on all templates, so return true
  return dynamicFormToTemplateMapping?.[dynamicFormKey] === template;
};

export const serviceOptions: { label: string; value: SessionServiceType }[] = [
  { label: sessionServiceOptionsDict.audio_and_video, value: 'audio_and_video' },
  { label: sessionServiceOptionsDict.in_person, value: 'in_person' }
];

export const participantOptions: { label: string; value: SessionParticipation }[] = [
  { label: sessionParticipantOptionsDict.client_only, value: 'client_only' },
  { label: sessionParticipantOptionsDict.client_and_family_member, value: 'client_and_family_member' },
  { label: sessionParticipantOptionsDict.family_member, value: 'family_member' }
];

export const getStartAndEndTimeInput = (fromDatetime: string, tz: string, start: string, end: string) => { // string format HH:mm:ss
  const baseDate = CalendarDateService.date(fromDatetime).tz(tz).startOf('D');
  const startDate = CalendarDateService.date(start, TIME_RANGE_DATE_VALUE_FORMAT);
  const endDate = CalendarDateService.date(end, TIME_RANGE_DATE_VALUE_FORMAT);
  const endIsBeforeStart = endDate.isBefore(startDate);

  const startTime = baseDate.set('hour', startDate.hour()).set('minute', startDate.minute());
  const endTime = baseDate.set('hour', endDate.hour()).set('minute', endDate.minute()).add(endIsBeforeStart ? 1 : 0, 'day');

  return { startTime: startTime.toISOString(), endTime: endTime.toISOString() };
};

export const getSelectItemsFromDictList = (dictList: string[]) => {
  return dictList.map((item) => ({ value: item, label: item }));
};

export const getAutocompleteOptionsFromDictList = (dictList: string[]) => {
  return dictList.map((item) => ({ value: item, label: item, id: item }));
};

const dynamicFormsKeys: ClinicalNotesDynamicFormKeyType[] = [ 'mentalStatusExam', 'riskAssessment', 'initialProgress', 'followUpProgress', 'presentingProblems' ];

export const getDynamicFormsObjectFromFormObject = (values: ClinicalNotesFormType) => {
  const dynamicForms = dynamicFormsKeys.reduce((r, key) => {
    return { ...r, [key]: values[key] };
  }, {} as ClinicalNotesDynamicFormsType);

  return { dynamicForms, dynamicFormsKeys };
};
export const getSessionFormsFromSessionNotes = (notes: SessionNotes) => {
  const sessionForms = dynamicFormsKeys.reduce((r, key) => {
    return { ...r, [key]: notes?.[key] };
  }, {} as ClinicalNotesSessionForms);

  return { sessionForms, dynamicFormsKeys };
};

// Be very careful touching anything below

// YesNo Helpers
export const getYesNoFieldKeyFromFieldKey = (fieldKey: string, parentKey: string) => `${parentKey}|${fieldKey}YesNo`;
export const deserializeYesNoFieldKey = (yesNoFieldKey: string) => {
  const length = 'YesNo'.length;
  const substr = yesNoFieldKey.slice(0, -length);
  const [ parentKey, fieldKey ] = substr.split('|');

  return { parentKey, fieldKey };
};
export const getYesNoFromBooleanValue = (value: boolean) => value ? YesNo.Yes : YesNo.No;
export const getYesNoForDynamicFormFromOptedValue = (value: boolean | null, dynamicFormVisibilityMappingKey: DynamicFormVisibilityKeys) => {
  if (value === null) return null;
  const dynamicForm = dynamicFormVisibilityMapping[dynamicFormVisibilityMappingKey];

  return getYesNoFromBooleanValue(value ? dynamicForm.visibleOnValue : !dynamicForm.visibleOnValue);
};
export const getOptedValueFromDynamicFormYesNo = (value: YesNo | null, dynamicFormVisibilityMappingKey: DynamicFormVisibilityKeys) => {
  if (value === null) return null;
  const dynamicForm = dynamicFormVisibilityMapping[dynamicFormVisibilityMappingKey];

  return dynamicForm.visibleOnYesNoValue === value;
};
// END YesNo Helpers

// Initial Values Helpers
export const getDynamicFormsInitialValues = (notes: SessionNotes) => {
  const { sessionForms, dynamicFormsKeys } = getSessionFormsFromSessionNotes(notes);

  const dynamicYesNoForm: { [key: string]: YesNo | null } = {};
  const dynamicYesNoKeyHash: DynamicYesNoKeyHash = {};

  const dynamicForm = dynamicFormsKeys.reduce((r, formKey: ClinicalNotesDynamicFormKeyType) => {
    const form = sessionForms[formKey];

    const dynamicFormObj = form.formFields.reduce((r, field) => {
      const { id: fieldKey, toggleVisibility } = field;

      const fieldTypeHelpers = getFieldTypeHelpers(field);

      const initialValue = fieldTypeHelpers.initialFormValue;

      if (toggleVisibility) {
        const key = getYesNoFieldKeyFromFieldKey(fieldKey, formKey);
        const yesNo = getYesNoFromBooleanValue(toggleVisibility.visibleOnValue);

        const yesNoInitial = fieldTypeHelpers.checkIfValueIsEmpty(initialValue) ? null : yesNo;

        if (yesNoInitial !== null) {
          dynamicYesNoForm[key] = yesNoInitial;
          dynamicYesNoKeyHash[key] = true;
        }
      }

      return { ...r, [fieldKey]: initialValue };
    }, {} as ClinicalNotesDynamicFormType);

    return { ...r, [formKey]: dynamicFormObj };
  }, {} as ClinicalNotesDynamicFormsType);

  return { dynamicForm: { ...dynamicForm, ...dynamicYesNoForm }, dynamicYesNoKeyHash };
};

export const treatmentObjInitialValue: ClinicalNotesTreatmentType = { treatmentApproachId: '', interventionIds: [] };
export const getTreatmentInitialValues = (approaches: SessionResource['notes']['treatment']['approachSelections']) => {
  if (!approaches || !approaches?.length) {
    return [ treatmentObjInitialValue ];
  } else {
    return approaches.map((a): ClinicalNotesTreatmentType => ({ treatmentApproachId: a.approach.id, interventionIds: a.interventions.map((i) => i.id) }));
  }
};
export const getDiagnosisInitialValue = (session?: Session, previousSession?: Session, allDiagnosis?: Diagnosis[]) => {
  if (session?.diagnosisIds?.length) {
    return session.diagnosisIds;
  } else if (previousSession?.diagnosisIds?.length) {
    if (allDiagnosis) {
      return previousSession?.diagnosisIds?.filter((id) => allDiagnosis.find((d) => d.id === id) !== undefined);
    }
    return previousSession?.diagnosisIds;
  } else {
    return [];
  }
};
// END Initial Values Helpers

// Input Values Helpers
export const getInputValuesFromDynamicFormsValues = (
  formValues: ClinicalNotesFormType,
  sessionForms: ClinicalNotesSessionForms
) => {
  const { dynamicForms, dynamicFormsKeys } = getDynamicFormsObjectFromFormObject(formValues);

  return dynamicFormsKeys.reduce((r, dynamicFormKey) => {
    const sessionForm = sessionForms[dynamicFormKey];
    const dynamicForm = dynamicForms[dynamicFormKey];

    const formDisabledByYesNoToggle = checkIfFormDisabledByYesNoToggle(dynamicFormKey, formValues);
    const formIsNotOnThisTemplate = !checkIfFormIsOnTemplate(dynamicFormKey, formValues.template);
    const discardForm = formDisabledByYesNoToggle || formIsNotOnThisTemplate;

    const newDynamicFormObj = Object.keys(dynamicForm).reduce((r, fieldKey) => {
      const value = dynamicForm[fieldKey];
      const sessionFormField = sessionForm.formFields.find(({ id }) => id === fieldKey); // could make a hash to slightly improve performance
      const { toggleVisibility, value: initialValueFromSessionForm } = sessionFormField;
      const fieldTypeHelpers = getFieldTypeHelpers(sessionFormField);

      let discardFormField = false;

      // if user selects values when input is visible, then changes visible value, this will make sure those chosen values aren't saved
      if (toggleVisibility) {
        const yesNoKey = getYesNoFieldKeyFromFieldKey(fieldKey, dynamicFormKey);
        const yesNoValue = formValues[yesNoKey] as YesNo;

        discardFormField = getYesNoFromBooleanValue(toggleVisibility.visibleOnValue) !== yesNoValue;
      }

      if (discardForm || discardFormField) return { ...r, [fieldKey]: initialValueFromSessionForm }; // this is a reset

      const newValue = fieldTypeHelpers.checkIfEmptyAndGetInputValue(value);

      return { ...r, [fieldKey]: newValue };
    }, {} as ClinicalNotesDynamicFormType);

    return { ...r, [dynamicFormKey]: newDynamicFormObj };
  }, {} as ClinicalNotesDynamicFormsType);
};
// END Input Values Helpers

const getBooleanFieldValue = (field: SessionFormField) => {
  if (field.fieldType === 'boolean') {
    if (field.renderAs === 'yesNo') {
      return field.value;
    }
    if (field.renderAs === 'radio') {
      return getYesNoFromBooleanValue(field.value);
    }
  }
  return null;
};

export const getFieldTypeHelpers: FieldTypeHelpersType = (formField: ClinicalNotesSessionFormField) => {
  switch (formField.fieldType) {
    case 'select_string':
    case 'text_area':
      return {
        initialFormValue: formField.value ?? '',
        checkIfEmptyAndGetInputValue: (formValue) => formValue === '' ? formField.defaultValue : formValue,

        checkIfValueIsEmpty: (formValue) => formValue === '',
      };
    case 'select_multiple_string':
      return {
        initialFormValue: Array.isArray(formField.value) ? formField.value : [],
        checkIfEmptyAndGetInputValue: (formValue) => !Array.isArray(formValue) ? [] : formValue,

        checkIfValueIsEmpty: (formValue) => {
          if (formField.noSelectionOptionText) return false;
          return Array.isArray(formValue) && !formValue.length;
        }
      };
    case 'boolean':
      return {
        initialFormValue: formField.value !== null ? getBooleanFieldValue(formField) : null,
        checkIfEmptyAndGetInputValue: (formValue) => {
          if (formValue === null) {
            return formField.defaultValue;
          } else {
            if (formField.renderAs === 'yesNo') {
              return formValue;
            }
            return formValue === YesNo.Yes;
          }
        },

        checkIfValueIsEmpty: (formValue) => formValue === null
      };
  }
};
